<template>
  <v-row
    class="actions-row flex-grow-1 ma-0"
    :class="{ 'justify-space-between': hasLeftBtns, 'justify-end': !hasLeftBtns }"
  >
    <div v-if="hasLeftBtns">
      <div class="btn-container">
        <slot name="left-btns" />
      </div>
    </div>

    <div v-if="hasRightButtons" class="d-flex align-center">
      <slot name="right-left-btns" />

      <div v-if="showExport" class="btn-container btn-container" :class="displayDiscard">
        <template v-if="exportOptions.length">
          <MainDropdownMenu
            v-bind="$attrs"
            :actions="exportOptions"
            location="top"
            title="Export"
            icon="mdi-swap-vertical"
          />
        </template>
        <template v-else-if="isCustomExport">
          <v-btn
            color="primary"
            max-height="28"
            variant="flat"
            :disabled="!isExportEnabled"
            @click="$emit('export')"
          >
            {{ exportButtonTitle !== '' ? exportButtonTitle : $t('actions.export') }}
            <v-icon end> mdi-swap-vertical </v-icon>
          </v-btn>
        </template>
        <template v-else>
          <LiveDataExport
            :data="liveData"
            :service-name="exportService"
            :include-scenario-in-export-name="includeScenarioInExportName"
          />
        </template>
      </div>

      <div v-if="showSeparator">
        <div class="action--separator" />
      </div>

      <div class="action-btn-container d-flex ml-3">
        <slot name="right-btns" />
        <div v-if="showDiscard" class="outlined-btn-container">
          <v-btn
            id="discardChangesBtn"
            variant="outlined"
            :disabled="!isDiscardEnabled || !hasDataChanges"
            secondary
            max-height="28"
            class="outlined-btn"
            @click="$emit('discard')"
          >
            {{ $t('actions.discardChanges') }}
          </v-btn>
        </div>
        <div v-if="showProceedButton" class="save-btn-container">
          <v-btn
            data-id-e2e="btnProceedSpace"
            variant="flat"
            :disabled="!hasDataChanges || hasDataErrors"
            max-height="28"
            action
            :loading="proceedLoading"
            @click="proceed"
          >
            {{ $t(`actions.${proceedBtnText}`) }}
          </v-btn>
        </div>
        <div v-if="showSaveButton" class="save-btn-container">
          <v-btn
            data-id-e2e="btnSaveSpace"
            variant="flat"
            :disabled="!hasDataChanges || hasDataErrors || saveDisabled"
            max-height="28"
            action
            min-width="64"
            :loading="saveLoading"
            @click="onSaveClickDebounced"
          >
            {{ $t(`actions.${saveBtnText}`) }}
          </v-btn>
        </div>
      </div>
      <slot name="right-end-btns" />
    </div>
  </v-row>
</template>

<script>
import { debounce } from 'lodash';

export default {
  props: {
    hasDataChanges: {
      type: Boolean,
      required: true,
    },
    hasDataErrors: {
      type: Boolean,
      required: true,
    },
    saveDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    showExport: {
      type: Boolean,
      required: false,
    },
    showDiscard: {
      type: Boolean,
      required: false,
      default: true,
    },
    saveLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    saveBtnText: {
      type: String,
      required: false,
      default: 'save',
    },
    proceedLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    proceedBtnText: {
      type: String,
      required: false,
      default: 'proceed',
    },
    liveData: {
      type: Array,
      required: false,
      default: () => [],
    },
    exportService: {
      type: String,
      required: false,
      default: () => '',
    },
    showSaveButton: {
      type: Boolean,
      required: false,
      default: true,
    },
    showProceedButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    exportOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    isCustomExport: {
      type: Boolean,
      required: false,
      default: false,
    },
    isExportEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    exportButtonTitle: {
      type: String,
      required: false,
      default: () => '',
    },
    isDiscardEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    includeScenarioInExportName: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showConfirmDialog: false,
      onSaveClickDebounced: debounce(() => this.save(), 200, { maxWait: 500 }),
    };
  },
  computed: {
    displayDiscard() {
      return !this.showDiscard ? 'hide-discard' : '';
    },
    hasLeftBtns() {
      return Boolean(this.$slots['left-btns']);
    },
    hasDefaultRightElements() {
      return this.showExport || this.showDiscard || this.showProceedButton || this.showSaveButton;
    },
    hasCustomRightElements() {
      const rightEndBtns = Boolean(this.$slots['right-end-btns']);
      const rightBtns = Boolean(this.$slots['right-btns']);
      const rightLeftBtns = Boolean(this.$slots['right-left-btns']);

      return rightEndBtns || rightBtns || rightLeftBtns;
    },
    hasRightButtons() {
      return this.hasCustomRightElements || this.hasDefaultRightElements;
    },
    showSeparator() {
      return (
        (Boolean(this.$slots['right-left-btns']) || this.showExport) &&
        (Boolean(this.$slots['right-btns']) ||
          Boolean(this.$slots['right-end-btns']) ||
          this.showDiscard ||
          this.showProceedButton ||
          this.showSaveButton)
      );
    },
  },
  methods: {
    save() {
      this.$emit('save');
      this.closeConfirmDialog();
    },
    proceed() {
      this.$emit('proceed');
      this.closeConfirmDialog();
    },
    closeConfirmDialog() {
      this.showConfirmDialog = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.actions-row {
  flex: 0;
  padding: 5px 0;
  .v-btn {
    margin: 2px 6px;
  }
  .btn-container {
    padding: 0px 14px;

    &.hide-discard {
      padding-left: 14px;
      padding-right: 8px;
    }
  }

  .action--separator {
    width: 1px;
    height: 28px; // or match your button height
    background-color: $assortment-horizontal-border-colour;
    margin: 0 12px;
    display: inline-block;
  }
  .action-btn-container {
    margin-right: 8px;
  }

  .save-btn-container {
    align-self: center;
    padding-left: 8px;
  }

  .outlined-btn-container {
    align-self: center;
    display: inline-block;

    &:not(:first-child) {
      padding-left: 8px;
    }
  }

  .v-btn > .v-btn__content .v-icon {
    color: white !important;
  }
}
</style>
